// RoomContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from "react";
import {
  useCreateRoomCategory,
  useRoomCategories,
  useRoomCategory,
  useUpdateRoomCategory,
} from "@/hooks/useRoomCategories";
import { RoomCategoryFilters } from "@/types/roomCategory";

// Sorting
type SortField = "number" | "type" | "status" | "floor";
type SortDirection = "asc" | "desc" | null;

interface RoomCategoriesContextType {
  // Query Hooks
  useRoomCategories: (
    hotelId: string,
    page?: number,
    limit?: number,
    filters?: RoomCategoryFilters
  ) => ReturnType<typeof useRoomCategories>;
  useRoomCategory: (
    id: string,
    hotelId: string
  ) => ReturnType<typeof useRoomCategory>;
  // Mutation Hooks
  useCreateRoomCategory: () => ReturnType<typeof useCreateRoomCategory>;
  useUpdateRoomCategory: (
    id: string,
    hotelId: string
  ) => ReturnType<typeof useUpdateRoomCategory>;

  // Filters
  filters: {
    amenities: string[];
    categoryId: string;
    currentPage: number;
    sortField: SortField | null;
    sortDirection: SortDirection;
  };

  // Actions
  actions: {
    setCategoryId: (value: string) => void;
    setAmenities: (value: string[]) => void;
    setCurrentPage: (page: number) => void;
    setSortField: (field: SortField | null) => void;
    setSortDirection: (dir: SortDirection) => void;
    handleSort: (field: string) => void;
    handleFilterChange: <T>(setter: (value: T) => void, value: T) => void;
    resetFilters: () => void;
  };

  // Computed
  computed: {
    apiFilters: RoomCategoryFilters;
    limit: number;
  };
}

const RoomCategoriesContext = createContext<
  RoomCategoriesContextType | undefined
>(undefined);

interface RoomCategoryProviderProps {
  children: ReactNode;
}

export const RoomCategoriesProvider: React.FC<RoomCategoryProviderProps> = ({
  children,
}) => {
  const [categoryId, setCategoryId] = useState<string>("");
  const [amenities, setAmenities] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const limit = 10;

  const handleSort = (field: string) => {
    const newSortField = field as SortField;

    if (sortField === newSortField) {
      let nextDirection: SortDirection;

      if (sortDirection === "asc") {
        nextDirection = "desc";
      } else if (sortDirection === "desc") {
        nextDirection = null;
      } else {
        nextDirection = "asc";
      }

      setSortDirection(nextDirection);

      if (nextDirection === null) {
        setSortField(null);
      }
    } else {
      setSortField(newSortField);
      setSortDirection("asc");
    }

    setCurrentPage(1);
  };

  function handleFilterChange<T>(setter: (value: T) => void, value: T) {
    setter(value);
    setCurrentPage(1);
  }

  const resetFilters = () => {
    setCategoryId("");
    setAmenities(["all features"]);
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
  };

  const apiFilters: RoomCategoryFilters = useMemo(() => {
    const filters: RoomCategoryFilters = {};
    if (amenities.length !== 0) filters.amenities = amenities;
    if (categoryId && categoryId !== "all categories")
      filters.categoryId = categoryId;
    if (sortField && sortDirection) {
      filters.sort = `${sortField}:${sortDirection}`;
    }
    filters.page = currentPage;
    filters.limit = limit;
    return filters;
  }, [amenities, categoryId, sortField, sortDirection, currentPage]);

  const contextValue: RoomCategoriesContextType = {
    useRoomCategories,
    useRoomCategory,
    useCreateRoomCategory,
    useUpdateRoomCategory,
    filters: {
      categoryId,
      amenities,
      currentPage,
      sortField,
      sortDirection,
    },
    actions: {
      setCategoryId,
      setAmenities,
      setCurrentPage,
      setSortField,
      setSortDirection,
      handleSort,
      handleFilterChange,
      resetFilters,
    },
    computed: {
      apiFilters,
      limit,
    },
  };

  return (
    <RoomCategoriesContext.Provider value={contextValue}>
      {children}
    </RoomCategoriesContext.Provider>
  );
};

export const useRoomCategoriesContext = () => {
  const context = useContext(RoomCategoriesContext);
  if (!context) {
    throw new Error(
      "useRoomCategoriesContext must be used within a RoomProvider"
    );
  }
  return context;
};
